// PictureGrid (the price book round, DEC-019; concept B's pictures): pictures as the thing you choose.
// One frame divided by hairlines, a tile each: its picture, its name and price, and a line that says
// what it needs (No customer words) or how many are on the option already ("On B: 1"). Press a tile to
// open it; its + adds it ("Add to B"), and the tile counts it. The shelf is the same grid at the size
// of a system: a picture and its name, to browse into. Not for a list to act on (DataTable) or one
// choice among a few (PictureChoice). Reads only --picgrid-*; Picture reads its own.
import { Glyph } from '../Glyph/Glyph.tsx';
import { Picture, type PictureSrc, type PictureState } from '../Picture/Picture.tsx';
import './PictureGrid.css';

export interface GridTile {
  id: string;
  name: string;
  picture?: PictureSrc | null;
  pictureState?: PictureState;
  price?: string;
  /** A line under it: what it needs, in words (red for a problem). */
  note?: { text: string; tone?: 'muted' | 'bad' | 'waiting' };
  /** How many are on what's being built ("On B: 1"). */
  count?: number;
}

export interface PictureGridProps {
  /** What the tiles are, for screen readers ("Water heaters › Tank, replace"). */
  label: string;
  tiles: GridTile[];
  /** items: a tile with a price; shelf: a system, to browse into. */
  density?: 'items' | 'shelf';
  onOpen?: (id: string) => void;
  /** With onAdd each tile has its +, said "Add to B: 50 gal gas water heater". */
  onAdd?: (id: string) => void;
  addLabel?: string;
  /** How the count reads ("On B"). */
  countLabel?: string;
}

export function PictureGrid({
  label,
  tiles,
  density = 'items',
  onOpen,
  onAdd,
  addLabel = 'Add',
  countLabel = 'On it',
}: PictureGridProps) {
  return (
    <ul className="fl-picgrid" data-density={density} aria-label={label}>
      {tiles.map((t) => {
        const face = (
          <>
            <Picture src={t.picture} state={t.pictureState} size={density === 'shelf' ? 'tile' : 'fill'} />
            <span className="fl-picgrid__name">{t.name}</span>
            {t.price ? <span className="fl-picgrid__price">{t.price}</span> : null}
            {t.note ? (
              <span className="fl-picgrid__note" data-tone={t.note.tone}>
                {t.note.text}
              </span>
            ) : null}
            {t.count ? (
              <span className="fl-picgrid__count">
                {countLabel}: {t.count}
              </span>
            ) : null}
          </>
        );
        return (
          <li key={t.id} className="fl-picgrid__tile" data-on={t.count ? '' : undefined}>
            {onOpen ? (
              <button type="button" className="fl-picgrid__face" onClick={() => onOpen(t.id)}>
                {face}
              </button>
            ) : (
              <span className="fl-picgrid__face">{face}</span>
            )}
            {onAdd ? (
              <button
                type="button"
                className="fl-picgrid__add"
                aria-label={`${addLabel}: ${t.name}`}
                onClick={() => onAdd(t.id)}
              >
                <Glyph name="plus" className="fl-picgrid__glyph" />
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
